import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, TextField, Button, Stack, CircularProgress } from '@mui/material';
import { AuthService } from '../../../services/auth.service';
import { DashboardService } from '../../../services/dashboard.service';
import { useToast } from '../../../context/ToastContext';
import { helpElement } from '../../../help/attrs';

const WIRED_KPIS: { key: string; label: string; helpKey: string }[] = [
    { key: 'attendance.onTimeRate', label: 'On-time rate target (%)', helpKey: 'dashboard.targets.onTime' },
    { key: 'attendance.presentRate', label: 'Attendance rate target (%)', helpKey: 'dashboard.targets.present' },
    { key: 'people.headcount', label: 'Headcount target', helpKey: 'dashboard.targets.headcount' },
];

export default function KpiTargetsSettings() {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution();
    const [values, setValues] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let live = true;
        DashboardService.getKpiTargets(institutionId)
            .then(d => {
                if (!live) return;
                const map: Record<string, string> = {};
                d.targets.forEach(t => { map[t.kpiKey] = String(t.targetValue); });
                setValues(map);
            })
            .catch(err => toast.showApiError(err, 'Failed to load KPI targets'))
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            // The PUT is the full desired set: a blank (or non-positive) field is omitted, which
            // clears that target on the server — this is how a saved target gets removed.
            const targets = WIRED_KPIS
                .filter(k => values[k.key] !== undefined && values[k.key] !== '' && Number(values[k.key]) > 0)
                .map(k => ({ kpiKey: k.key, targetValue: Number(values[k.key]) }));
            await DashboardService.setKpiTargets(institutionId, targets);
            toast.showSuccess('KPI targets saved');
        } catch (err) {
            toast.showApiError(err, 'Failed to save KPI targets');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: 3, maxWidth: 480 }} data-help-page="dashboard.targets">
            <Typography variant="h5" sx={{ mb: 1 }}>KPI Targets</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} data-help-section="dashboard.targets.intro">
                Set a goal for each metric — dashboard tiles show progress against it once a target is set.
                Leave a field blank to remove its target.
            </Typography>
            <Paper sx={{ p: 3 }} data-help-section="dashboard.targets.form">
                <Stack spacing={2}>
                    {WIRED_KPIS.map(k => (
                        <TextField
                            key={k.key}
                            {...helpElement(k.helpKey)}
                            label={k.label}
                            type="number"
                            value={values[k.key] ?? ''}
                            onChange={(e) => setValues(prev => ({ ...prev, [k.key]: e.target.value }))}
                            fullWidth
                        />
                    ))}
                    <Button data-help="dashboard.targets.save" variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save targets'}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}
